import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Calendar, MapPin, User, Lightbulb } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import type { ContactLog } from "@/types/api";
import { suggestionsApi } from "@/api/suggestions";

interface MemoryCardProps {
  memory: ContactLog;
  variant?: "default" | "compact";
  showSeparator?: boolean;
  showAvatar?: boolean;
  showSuggestionCount?: boolean;
}

const MemoryCard = ({ memory, variant = "default", showSeparator = false, showAvatar = false, showSuggestionCount = false }: MemoryCardProps) => {
  const navigate = useNavigate();
  const [suggestionCount, setSuggestionCount] = useState<number>(0);

  useEffect(() => {
    if (!showSuggestionCount) return;

    const fetchSuggestionCount = async () => {
      try {
        const suggestions = await suggestionsApi.list({
          memory_id: memory.id,
          page_size: 100
        });

        setSuggestionCount(suggestions.count || suggestions.results?.length || 0);
      } catch (error) {
        console.error('Error fetching suggestion count:', error);
      }
    };

    fetchSuggestionCount();
  }, [memory.id, showSuggestionCount]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: variant === "compact" ? 'short' : 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (variant === "compact") {
    // Compact version for sidebar/related memories
    return (
      <div
        onClick={() => navigate(`/memories/${memory.id}`)}
        className="cursor-pointer group p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
      >
        {showAvatar && (
          <div className="flex items-center gap-2 mb-2">
            <Avatar className="w-8 h-8">
              {memory.contact_avatar ? (
                <AvatarImage
                  src={`/images/avatars/${memory.contact_avatar}`}
                  alt={memory.contact_name}
                />
              ) : null}
              <AvatarFallback className="bg-gray-200">
                <User className="w-4 h-4 text-gray-500" />
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-gray-900">{memory.contact_name}</span>
          </div>
        )}

        {/* Content Preview */}
        <p className="text-sm text-gray-700 mb-2 line-clamp-3">
          {memory.content}
        </p>

        {/* Date, Location, Mood and Suggestion Count */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(memory.log_date)}</span>
            {memory.location && (
              <>
                <span>•</span>
                <MapPin className="w-3 h-3" />
                <span>{memory.location}</span>
              </>
            )}
            {memory.mood && (
              <>
                <span>•</span>
                <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">
                  {memory.mood}
                </span>
              </>
            )}
          </div>
          {showSuggestionCount && suggestionCount > 0 && (
            <div className="flex items-center gap-1 text-gray-700 whitespace-nowrap">
              <Lightbulb className="w-3 h-3 text-blue-600" />
              <span>{suggestionCount} suggestion{suggestionCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default version for lists
  return (
    <>
      {showSeparator && <div className="border-t border-gray-200 my-4"></div>}
      <div
        className="pb-4 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
        onClick={() => navigate(`/memories/${memory.id}`)}
      >
        {showAvatar && (
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="w-10 h-10">
              {memory.contact_avatar ? (
                <AvatarImage
                  src={`/images/avatars/${memory.contact_avatar}`}
                  alt={memory.contact_name}
                />
              ) : null}
              <AvatarFallback className="bg-gray-200">
                <User className="w-5 h-5 text-gray-500" />
              </AvatarFallback>
            </Avatar>
            <span className="font-medium text-gray-900">{memory.contact_name}</span>
          </div>
        )}

        <div className="flex items-start justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">
            {formatDate(memory.log_date)}
          </p>
          {memory.mood && (
            <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700">
              {memory.mood}
            </span>
          )}
        </div>
        <p className="text-gray-600 mb-2">{memory.content}</p>
        <div className="flex items-center justify-between">
          {memory.location && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MapPin className="w-4 h-4" />
              <span>{memory.location}</span>
            </div>
          )}
          {showSuggestionCount && suggestionCount > 0 && (
            <div className="flex items-center gap-1 text-sm text-gray-700 whitespace-nowrap">
              <Lightbulb className="w-4 h-4 text-blue-600" />
              <span>{suggestionCount} suggestion{suggestionCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MemoryCard;
